import React from 'react';
import { useTheme } from '@mui/material';
import type { ECharts } from 'echarts/core';
import { Chart, DownloadButton, Panel } from '../../../../ui-kit';
import { downloadChartImage, type ChartImageFormat } from '../../../../utils/chartDownload';
import type { PlannerDay } from '../../../../types/plannerDashboard';
import { oneDecimal, whole } from '../plannerFormat';

/**
 * Each day's best conversion against the target line. Days that miss are
 * drawn in the danger colour and marked ✕ under their name, so the result
 * does not rest on colour alone.
 */
export default function ConversionChart({
  days, target, loading,
}: { days: PlannerDay[]; target: number; loading?: boolean }) {
  const theme = useTheme();
  const { tokens } = theme;
  const chartRef = React.useRef<ECharts | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);

  const download = (format: ChartImageFormat) => {
    setProblem(null);
    try {
      downloadChartImage(chartRef.current, format, 'Conversion by day');
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'The chart could not be downloaded.');
    }
  };

  const top = Math.max(20, Math.ceil(Math.max(target, ...days.map((d) => d.conversion ?? 0)) / 5) * 5 + 5);

  const chart = (fill: boolean) => {
    const option = {
      grid: { left: 8, right: 72, top: 28, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (items: { dataIndex: number }[]) => {
          const d = days[items[0].dataIndex];
          return [
            `<b>${d.name}</b>`,
            `Conversion: ${d.conversion === null ? '—' : `${oneDecimal(d.conversion)}%`}`,
            `Sales Leads contacted a day: ${oneDecimal(d.reached)}`,
            `Submits a day: ${oneDecimal(d.applied)}`,
            `Sales Leads covered: ${whole(d.sales_leads_used)} of ${whole(d.sales_leads_needed)}`,
            d.hits_target ? 'Hits the target' : 'Under the target',
          ].join('<br/>');
        },
      },
      // Each day's figure sits under its name rather than on the bar: a bar
      // under the target ends just below the dashed line, where a label would
      // collide with it, and inside a narrow bar it does not fit.
      xAxis: {
        type: 'category',
        data: days.map((d) => d.short_name),
        axisTick: { show: false },
        axisLabel: {
          interval: 0,
          formatter: (_: string, index: number) => {
            const d = days[index];
            const figure = d.conversion === null ? '—' : `${oneDecimal(d.conversion)}%`;
            return `{day|${d.short_name}}\n{${d.hits_target ? 'hit' : 'miss'}|${figure}${d.hits_target ? ' ✓' : ' ✕'}}`;
          },
          rich: {
            day: { color: tokens.text.secondary, fontSize: 12, lineHeight: 20 },
            hit: { color: tokens.text.primary, fontSize: 13, fontWeight: 600, lineHeight: 18 },
            miss: { color: tokens.semantic.danger, fontSize: 13, fontWeight: 600, lineHeight: 18 },
          },
        },
      },
      yAxis: {
        type: 'value',
        max: top,
        interval: 5,
        axisLabel: { formatter: '{value}%' },
      },
      series: [
        {
          name: 'Conversion',
          type: 'bar',
          barMaxWidth: 56,
          data: days.map((d) => ({
            value: d.conversion === null ? 0 : Math.round(d.conversion * 10) / 10,
            itemStyle: d.hits_target
              ? { color: tokens.brand.primary, borderRadius: [4, 4, 0, 0] }
              : {
                  color: tokens.semantic.dangerSoft,
                  borderColor: tokens.semantic.danger,
                  borderWidth: 1.5,
                  borderRadius: [4, 4, 0, 0],
                },
          })),
          markLine: {
            symbol: 'none',
            silent: true,
            lineStyle: { color: tokens.text.secondary, type: 'dashed', width: 2 },
            label: { formatter: `Target ${whole(target)}%`, color: tokens.text.primary, fontWeight: 600, position: 'end' },
            data: [{ yAxis: target }],
          },
        },
      ],
    };
    return (
      <Chart
        option={option}
        height={fill ? undefined : 260}
        fill={fill}
        onReady={fill ? undefined : (instance) => { chartRef.current = instance; }}
        ariaLabel={`Conversion by day against a ${whole(target)}% target`}
      />
    );
  };

  return (
    <Panel
      title="Conversion by day"
      subtitle="Submits ÷ Sales Leads contacted, for the Sales Leads each day uses. ✓ hits the target, ✕ falls under it."
      actions={
        <DownloadButton
          formats={[
            { key: 'png', label: 'Image (.png)', onDownload: () => download('png') },
            { key: 'svg', label: 'Vector (.svg)', description: 'Sharp at any size', onDownload: () => download('svg') },
          ]}
          disabled={loading || days.length === 0}
        />
      }
      footer={problem}
      loading={loading}
      loadingHeight={260}
      expanded={{ content: chart(true) }}
    >
      {chart(false)}
    </Panel>
  );
}
